import { AfterViewInit, Component, NgZone, OnInit, inject } from '@angular/core';
import { AsyncPipe, NgClass } from '@angular/common';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';

import { App } from '@capacitor/app';
import { SafeArea, SystemBarsStyle } from '@capacitor-community/safe-area';
import { SplashScreen } from '@capacitor/splash-screen';
import { Intercom, update } from '@intercom/messenger-js-sdk';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { filter, take } from 'rxjs/operators';

import { environment } from '../environments/environment';

import { ActiveSubscriptionService } from './core/services/active-subscription.service';
import { AppPushNotificationsService } from './core/services/app-push-notifications.service';
import { AsyncCommandService } from './core/services/async-command.service';
import { BackButtonService } from './core/services/back-button.service';
import { ChallengeService } from './core/services/challenge.service';
import { ColorService } from './core/services/color.service';
import { ConversationService } from './core/services/conversation.service';
import { EmailPermanentFailureService } from './core/services/email-permanent-failure.service';
import { EnvironmentService } from './core/services/environment.service';
import { FeedbackService } from './core/services/feedback.service';
import { FrontAppVersionAppService } from './core/services/front-app-version-app.service';
import { FrontAppVersionSiteService } from './core/services/front-app-version-site.service';
import { IsApplicationService } from './core/services/is-application.service';
import { KnowledgeAnchoringService } from './core/services/knowledge-anchoring.service';
import { LanguageService } from './core/services/language.service';
import { LastDeployedVersionsService } from './core/services/last-deployed-versions.service';
import { PusherUserChannelService } from './core/services/pusher-user-channel.service';
import { SeoService } from './core/services/seo.service';
import { WebsiteNotificationsService } from './core/services/website-notifications.service';

import { AuthActions, AuthSelectors } from './core/state/auth-state';

import { screenWidthConstants } from './shared/utils/screen-sizes';
import { AcceptCookiesComponent } from './shared/components/root/accept-cookies/accept-cookies.component';
import { InternetStatusComponent } from './shared/components/root/internet-status/internet-status.component';
import { RootBadgeComponent } from './shared/components/root/root-badge/root-badge.component';
import { LoaderHashtagComponent } from './shared/components/loaders/loaders/loader-hashtag/loader-hashtag.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  imports: [
    AsyncPipe,
    NgClass,
    RouterOutlet,
    AcceptCookiesComponent,
    LoaderHashtagComponent,
    InternetStatusComponent,
    RootBadgeComponent,
  ],
  host: {
    '(window:resize)': 'onResize($event)',
  },
})
export class AppComponent implements OnInit, AfterViewInit {
  private ngZone = inject(NgZone);
  private router = inject(Router);
  private store = inject(Store);
  private translate = inject(TranslateService);
  private activeSubscriptionService = inject(ActiveSubscriptionService);
  private appPushNotificationsService = inject(AppPushNotificationsService);
  private asyncCommandService = inject(AsyncCommandService);
  private backButtonService = inject(BackButtonService);
  private challengeService = inject(ChallengeService);
  private colorService = inject(ColorService);
  private conversationService = inject(ConversationService);
  private emailPermanentFailureService = inject(EmailPermanentFailureService);
  private environmentService = inject(EnvironmentService);
  private feedbackService = inject(FeedbackService);
  private frontAppVersionAppService = inject(FrontAppVersionAppService);
  private frontAppVersionSiteService = inject(FrontAppVersionSiteService);
  private isApplicationService = inject(IsApplicationService);
  private knowledgeAnchoringService = inject(KnowledgeAnchoringService);
  private pusherUserChannelService = inject(PusherUserChannelService);
  languageService = inject(LanguageService);
  private lastDeployedVersionsService = inject(LastDeployedVersionsService);
  private seoService = inject(SeoService);
  private websiteNotificationsService = inject(WebsiteNotificationsService);

  appIsInitializing$ = this.store.select(AuthSelectors.initializing);
  appIsLoading = true;

  impersonateUser$ = this.store.select(AuthSelectors.impersonateUser);
  examTestingState$ = this.store.select(AuthSelectors.examTestingState);

  constructor() {
    // Pour que ça ne s'applique que sur android
    if (
      this.isApplicationService.isApplication() &&
      !window.location.protocol.includes('capacitor')
    ) {
      SafeArea.setSystemBarsStyle({ style: SystemBarsStyle.Light });
    }

    this.translate.setDefaultLang(environment.defaultLanguage);

    Intercom({
      app_id: environment.intercomAppId,
      custom_launcher_selector: '#intercom_manual_launcher',
      hide_default_launcher: this.hideDefaultIntercomLauncher,
      language_override: environment.defaultLanguage,
      version: `${this.isApplicationService.isApplication() ? 'App' : 'Desktop'} ${
        this.environmentService.stringifiedFrontAppVersion
      }`,
      current_platform: this.isApplicationService.isApplication()
        ? this.isApplicationService.platform
        : 'web',
    });

    if (this.isApplicationService.isApplication()) {
      SplashScreen.hide();

      // handle deeplinks
      App.addListener('appUrlOpen', (data) => {
        this.ngZone.run(() => {
          const slug = data.url.split(environment.domain).pop();
          if (slug) {
            this.router.navigateByUrl(slug);
          }
        });
      });
    }

    this.pusherUserChannelService.init();
    this.activeSubscriptionService.init();
    this.appPushNotificationsService.init();
    this.asyncCommandService.init();
    this.backButtonService.init();
    this.challengeService.init();
    this.conversationService.init();
    this.colorService.init();
    this.emailPermanentFailureService.init();
    this.feedbackService.init();
    this.lastDeployedVersionsService.init();
    this.frontAppVersionAppService.init();
    this.frontAppVersionSiteService.init();
    this.knowledgeAnchoringService.init();
    this.websiteNotificationsService.init();

    this.appIsInitializing$
      .pipe(
        filter((initializing) => !initializing),
        take(1)
      )
      .subscribe(() => {
        setTimeout(() => {
          this.appIsLoading = false;
        }, 1000);
      });

    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      update({});
      this.computeIntercomDisplay();
    });
  }

  ngOnInit() {
    this.seoService.init();
  }

  // for intercom : when messenger is added to the dom, we need to change its bottom/top css properties for the iphone notch
  ngAfterViewInit(): void {
    setTimeout(() => {
      // nnkitodo [newdesign] vérifier
      if (this.isApplicationService.isApplication()) {
        const safeTop = getComputedStyle(document.documentElement).getPropertyValue(
          '--safe-area-inset-top'
        );
        const safeBottom = getComputedStyle(document.documentElement).getPropertyValue(
          '--safe-area-inset-bottom'
        );
        setInterval(() => {
          const intercomContainer = document.getElementById('intercom-container');
          if (intercomContainer) {
            const intercomMessengerFrame = document.getElementsByClassName(
              'intercom-messenger-frame'
            );
            if (intercomMessengerFrame.length) {
              (
                intercomMessengerFrame[0] as any
              ).style.cssText = `background-color: ${this.colorService.getSiteMainColor()}`;
              const iframe = intercomMessengerFrame[0].firstChild;
              if (iframe) {
                (
                  iframe as any
                ).style.cssText = `top: ${safeTop} !important; bottom: ${safeBottom} !important; height: calc(100% - ${safeTop} - ${safeBottom}) !important`;
              }
            }
          }
        }, 1000);
      }
    }, 5000);

    // nnkitodo [v2later] on peut aussi tenter ça à la place ?
    // if (this.isApplicationService.isApplication()) {
    //   const safeTop = getComputedStyle(document.documentElement).getPropertyValue(
    //     '--safe-area-inset-top'
    //   );
    //   const safeBottom = getComputedStyle(document.documentElement).getPropertyValue(
    //     '--safe-area-inset-bottom'
    //   );
    //   window.intercomSettings = {
    //     app_id: 'abc123',
    //     alignment: 'left',
    //     horizontal_padding: 20,
    //     vertical_padding: Math.max(safeTop, safeBottom),

    //     z_index: -15
    //   };
    // }
  }

  onResize(event: UIEvent) {
    this.computeIntercomDisplay();
  }

  // nnkitodo [newdesign] sur mobile/admin on a jamais intercom, il faudrait pouvoir adapter la variable safeIntercom
  get hideDefaultIntercomLauncher() {
    return (
      window.innerWidth < screenWidthConstants.screenMD ||
      this.router.url.search('/app/admin') !== -1
    );
  }

  computeIntercomDisplay() {
    update({ hide_default_launcher: this.hideDefaultIntercomLauncher });
  }

  stopImpersonateUser() {
    this.impersonateUser$.pipe(take(1)).subscribe((student) => {
      this.store.dispatch(AuthActions.stopImpersonateUser({ student: student.toResult() }));
    });
  }

  stopTestingExam() {
    this.examTestingState$.pipe(take(1)).subscribe((examTestingState) => {
      this.store.dispatch(AuthActions.stopTestingExam(examTestingState));
    });
  }
}
